<script setup lang="ts">
import { useGameWinnerTile, useThemeLogo, useI18n, ref } from '#imports'

const { t } = useI18n()
const brandIcon = useThemeLogo('icon')

const { winner, icon, money, wonTimeAgo, startGame } = useGameWinnerTile()

const useFallbackLogo = ref(false)
</script>

<template>
    <div class="winner-tile-long" @click="startGame()">
        <div class="winner-tile-long__game">
            <NuxtImg
                v-if="(useFallbackLogo || !icon?.trim()) && brandIcon"
                class="winner-tile-long__game-image"
                provider="cloudflare"
                preset="local"
                :src="brandIcon.src"
                :quality="brandIcon.quality"
                width="42"
                height="42"
                alt=""
                loading="lazy"
                decoding="async"
            />
            <NuxtImg
                v-else
                provider="cloudflare"
                preset="gameservice"
                loading="lazy"
                decoding="async"
                :src="icon"
                :alt="`${winner.wonBy || winner.username} ${t('game.gameWinnerTile.won')} ${money} - ${winner.gameName}`"
                :title="winner.gameName"
                width="42"
                height="42"
                quality="70"
                class="winner-tile-long__game-image"
                @error="useFallbackLogo = true"
            />

            <p class="winner-tile-long__game-name">
                {{ winner.gameName }}
            </p>
        </div>

        <div class="winner-tile-long__content">
            <p class="winner-tile-long__name">
                {{ winner.wonBy || winner.username }}
            </p>

            <p class="winner-tile-long__date">
                <Transition name="fade" appear>
                    <template v-if="wonTimeAgo">
                        {{ wonTimeAgo }}
                    </template>
                </Transition>
            </p>
        </div>

        <div class="winner-tile-long__amount">
            {{ money }}
        </div>
    </div>
</template>

<style scoped lang="scss">
.winner-tile-long {
    display: grid;
    grid-template: 'game content amount' 1fr / 116px 80px 1fr;
    grid-gap: 8px;
    font-size: 10px;
    font-weight: $text-bold;

    @media (min-width: $screen-xs) {
        grid-template: 'game content amount' 1fr / 132px 1fr 72px; // 72px = room for 1.000.000 kr.
        font-size: $font-size-xs;
    }

    @media (min-width: $screen-sm) {
        grid-template: 'game content amount' 1fr / 146px 1fr 72px;
    }

    @media (min-width: $screen-md) {
        grid-template: 'game content amount' 1fr / 2fr 2fr 2fr;
        font-size: $font-size-sm;
    }

    > div {
        align-self: center;
    }

    &:hover {
        cursor: pointer;
    }

    &__game {
        display: flex;
        grid-area: game;

        &-image {
            margin-right: 5px;
            border-radius: $border-radius;
            min-width: 42px;
            max-width: 42px;
            min-height: 42px;
            max-height: 42px;

            @media (min-width: $screen-md) {
                margin-right: 10px;
            }
        }

        &-name {
            align-self: center;
            margin: 0;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    &__content {
        grid-area: content;
        flex-direction: column;

        > p {
            margin: 0;
        }

        @media (min-width: $screen-md) {
            display: flex;

            > p {
                margin-right: 20px;
            }
        }
    }

    &__date {
        font-weight: $text-medium;
        min-height: 20px;
    }

    &__amount {
        grid-area: amount;
        justify-self: end;
    }
}
</style>
